import { n as removeTrapFocus, r as trapFocus } from "./moonbase-helpers.js";
//#region src/js/components/drawer.js
/**
* A custom HTML element that creates a side drawer component with nested menu support.
* Handles opening/closing animations, focus trapping, and keyboard navigation.
* @extends HTMLElement
*/
/**
* Creates an instance of SideDrawer.
* Initializes the main details toggle element and binds event listeners.
*/
/**
* Binds event listeners for keyboard navigation, focus management, and click events.
* @private
*/
/**
* Handles keyboard events, specifically the Escape key to close open drawers.
* @param {KeyboardEvent} event - The keyboard event object
* @private
*/
/**
* Handles click events on summary elements to toggle drawer and submenu states.
* Manages opening/closing animations, ARIA attributes, and focus trapping.
* @param {MouseEvent} event - The click event object
* @private
*/
/**
* Opens the side drawer with animations and focus management.
* @param {HTMLElement} summaryElement - The summary element that triggered the opening
* @private
*/
/**
* Closes the side drawer and all open submenus.
* @param {Event} event - The event that triggered the closing
* @param {HTMLElement|boolean} [elementToFocus=false] - The element to focus after closing
* @private
*/
/**
* Handles focus out events to automatically close the drawer when focus leaves.
* @private
*/
/**
* Handles click events on close buttons within the drawer.
* @param {MouseEvent} event - The click event object
* @private
*/
/**
* Closes a submenu and restores focus to the parent menu.
* @param {HTMLDetailsElement} detailsElement - The details element to close
* @private
*/
/**
* Animates the closing of a details element using requestAnimationFrame.
* @param {HTMLDetailsElement} detailsElement - The details element to animate
* @private
*/
/**
* A specialized drawer component for header navigation.
* Extends SideDrawer with header-specific positioning logic.
* @extends SideDrawer
*/
/**
* Opens the header drawer with header-specific positioning calculations.
* @param {HTMLElement} summaryElement - The summary element that triggered the opening
* @override
* @private
*/
/**
* Closes the header drawer and removes header-specific classes.
* @param {Event} event - The event that triggered the closing
* @param {HTMLElement} elementToFocus - The element to focus after closing
* @override
* @private
*/
var SideDrawer = class extends HTMLElement {
	constructor() {
		super();
		this.mainDetailsToggle = this.querySelector("details");
		this.bindEvents();
	}
	bindEvents() {
		this.addEventListener("keyup", this.onKeyUp.bind(this));
		this.addEventListener("focusout", this.onFocusOut.bind(this));
		this.querySelectorAll("summary").forEach((summary) => summary.addEventListener("click", this.onSummaryClick.bind(this)));
		this.querySelectorAll("button").forEach((button) => button.addEventListener("click", this.onCloseButtonClick.bind(this)));
	}
	onKeyUp(event) {
		if (event.code.toUpperCase() !== "ESCAPE") return;
		const openDetailsElement = event.target.closest("details[open]");
		if (!openDetailsElement) return;
		if (openDetailsElement === this.mainDetailsToggle) this.closeSideDrawer(event, this.mainDetailsToggle.querySelector("summary"));
		else this.closeSubmenu(openDetailsElement);
	}
	onSummaryClick(event) {
		const summaryElement = event.currentTarget;
		const detailsElement = summaryElement.parentNode;
		const parentMenuElement = detailsElement.closest(".has-submenu");
		const isOpen = detailsElement.hasAttribute("open");
		const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
		if (detailsElement === this.mainDetailsToggle) {
			if (isOpen) event.preventDefault();
			isOpen ? this.closeSideDrawer(event, summaryElement) : this.openSideDrawer(summaryElement);
			if (window.matchMedia("(max-width: 990px)").matches) document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
		} else setTimeout(() => {
			detailsElement.classList.add("menu-opening");
			summaryElement.setAttribute("aria-expanded", true);
			if (parentMenuElement) parentMenuElement.classList.add("submenu-open");
			const addTrap = () => {
				trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector("button"));
				summaryElement.nextElementSibling.removeEventListener("transitionend", addTrap);
			};
			if (!reducedMotion.matches) summaryElement.nextElementSibling.addEventListener("transitionend", addTrap);
			else addTrap();
		}, 100);
	}
	openSideDrawer(summaryElement) {
		setTimeout(() => {
			this.mainDetailsToggle.classList.add("menu-opening");
		});
		summaryElement.setAttribute("aria-expanded", true);
		trapFocus(this.mainDetailsToggle, summaryElement);
		document.documentElement.classList.add("overlay--open");
	}
	closeSideDrawer(event, elementToFocus = false) {
		if (event === void 0) return;
		this.mainDetailsToggle.classList.remove("menu-opening");
		this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
			details.removeAttribute("open");
			details.classList.remove("menu-opening");
		});
		this.mainDetailsToggle.querySelectorAll(".submenu-open").forEach((submenu) => {
			submenu.classList.remove("submenu-open");
		});
		document.documentElement.classList.remove("overlay--open");
		removeTrapFocus(elementToFocus);
		this.closeAnimation(this.mainDetailsToggle);
	}
	onFocusOut() {
		setTimeout(() => {
			if (this.mainDetailsToggle.hasAttribute("open") && !this.mainDetailsToggle.contains(document.activeElement)) this.closeSideDrawer();
		});
	}
	onCloseButtonClick(event) {
		const detailsElement = event.currentTarget.closest("details");
		this.closeSubmenu(detailsElement);
	}
	closeSubmenu(detailsElement) {
		const parentMenuElement = detailsElement.closest(".submenu-open");
		if (parentMenuElement) parentMenuElement.classList.remove("submenu-open");
		detailsElement.classList.remove("menu-opening");
		detailsElement.querySelector("summary").setAttribute("aria-expanded", false);
		removeTrapFocus(detailsElement.querySelector("summary"));
		this.closeAnimation(detailsElement);
	}
	closeAnimation(detailsElement) {
		let animationStart;
		const handleAnimation = (time) => {
			if (animationStart === void 0) animationStart = time;
			if (time - animationStart < 400) window.requestAnimationFrame(handleAnimation);
			else {
				detailsElement.removeAttribute("open");
				if (detailsElement.closest("details[open]")) trapFocus(detailsElement.closest("details[open]"), detailsElement.querySelector("summary"));
			}
		};
		window.requestAnimationFrame(handleAnimation);
	}
};
customElements.define("side-drawer", SideDrawer);
//#endregion
